/**
 * QMS controlled folders: the tree on the left, the open folder on the right.
 *
 * Ed, 2026-11-03: "one folder each, records only, its own rail item", and "folder grants give access". A folder places
 * records -- each record lives in exactly one -- and whoever a folder is given to can see what is filed in it,
 * read-only. Service: cres-aws-t1/cres_t1/service/folder.py.
 *
 * ROUTED, /folders/:usid, so a folder is a link somebody can send (detail pages are linkable).
 *
 * WHAT THIS PAGE CANNOT SHOW IT SAYS. A folder the persona cannot see is not drawn at all, not even its name; a folder
 * whose parent is hidden is drawn at the top of the tree; and records filed here that this persona cannot open are
 * counted, never named.
 *
 * THE RECORD LIST IS A PLAIN TABLE, NOT A PlatformTable, and that is a deliberate exception: PlatformTable is bound to
 * one enterprise component, and a folder holds records of every classification at once -- which is the point of it.
 * The approvals queue made the same call for the same reason.
 *
 * THE VIEW SITS ABOVE BOTH PANES (Ed, 2026-11-03), because it decides both: the tree lists the folders the view can
 * see, and the open folder lists the records it can open. Position, Management, Sub-Organization and Organization are
 * the tabs every record list draws, from the same availableModes, and a change reloads the tree and the folder.
 *
 * A RECORD'S OBJECT ID OPENS IT IN THE RECORD DRAWER, and the icon beside it opens the record page in a new tab: the
 * project's ObjectIdLink and RecordDrawer, so the folder is never left to read what is in it.
 */
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router";
import {Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton,
        InputAdornment, LinearProgress,
        MenuItem, Switch, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Tooltip,
        Typography} from "@mui/material";
import {ChevronRightOutlined, CloseOutlined, CreateNewFolderOutlined, DeleteOutlineOutlined, EditOutlined, ExpandMoreOutlined,
        FolderOpenOutlined, FolderOutlined, FolderSpecialOutlined, LockPersonOutlined, SearchOutlined} from "@mui/icons-material";

import PageHeader from "../components/Common/PageHeader";
import {ResizeGripper, useResizablePanel} from "../components/Common/ResizablePanel";
import RecordDrawer from "../components/Foundation/RecordDrawer";
import FolderContents, {Column, FolderView, ViewSwitch, itemsOf, rememberView, rememberedView} from "../components/Folders/FolderContents";
import FolderFilterPane, {FolderFilters, NO_FILTERS, filtering, filtersPayload, toggled} from "../components/Folders/FolderFilterPane";
import ObjectIdLink, {RecordDrawerContext} from "../components/Project/ObjectIdLink";
import PersonaHeaderInfo from "../components/Common/PersonaHeaderInfo";
import {AppContext} from "../util/AppContext";
import EventBus from "../util/EventBus";
import {availableModes} from "../util/VisibilityModes";
import {makeCresAxios} from "../util/Axios";
import {classificationByCode} from "../util/objectClassifications";
import {Blue3, Grey5, Grey6, Grey7, Grey17, White} from "../theme/styles";

type Row = Record<string, any>;

export const LEVEL_WORDS: Record<string, string> = {VIEW: "View", FILE: "File", MANAGE: "Manage"};
export const LEVEL_MEANS: Record<string, string> = {
    VIEW: "See the folder and its records",
    FILE: "Also file and move records in",
    MANAGE: "Also rename, re-permission and make subfolders",
};
/** What a level lets its holder do, in full: each level's line says only what it adds to the one below. */
export function levelMeaning(level: string) {
    const order = Object.keys(LEVEL_MEANS);
    const upTo = order.slice(0, order.indexOf(level) + 1);
    if (!upTo.length) return level;
    return upTo.map((one, index) => index ? LEVEL_MEANS[one].replace(/^Also /, "").toLowerCase() : LEVEL_MEANS[one].toLowerCase())
               .join("; ");
}
const GRANTEE_TYPES = ["Position", "User Group", "Work Group"];

const ACT_WORDS: Record<string, string> = {
    CREATED: "Created", RENAMED: "Renamed", DESCRIBED: "Description changed", MOVED: "Moved",
    INHERITANCE: "Inheritance changed", GRANTED: "Given", UNGRANTED: "Taken away", FILED: "Record filed",
    UNFILED: "Record taken out", DELETED: "Deleted",
};

/** The reason a refusal came back with, or a sentence saying it did not. */
export function refusalOf(problem: any, fallback: string) {
    return problem?.response?.data?.Cause ?? fallback;
}

/** Folders as a tree: children grouped under their parent, roots under null, each level by name. */
export function childrenOf(folders: Row[]) {
    const out = new Map<string | null, Row[]>();
    for (const one of folders) {
        const key = one.ParentFolderUSID ?? null;
        out.set(key, [...(out.get(key) ?? []), one]);
    }
    out.forEach(list => list.sort((a, b) => String(a.Name).localeCompare(String(b.Name))));
    return out;
}

export function describeEvent(event: Row) {
    const detail = event.Detail ?? {};
    const words = ACT_WORDS[event.Act] ?? event.Act;
    if (event.Act === "RENAMED") return `${words}: ${detail.from} → ${detail.to}`;
    if (event.Act === "GRANTED") return `${words}: ${detail.name ?? detail.grantee_type} (${LEVEL_WORDS[detail.level] ?? detail.level})`;
    if (event.Act === "UNGRANTED") return `${words}: ${detail.grantee_type} (${LEVEL_WORDS[detail.level] ?? detail.level})`;
    if (event.Act === "INHERITANCE") return detail.inherit ? "Now takes the grants of the folders above" : "Stopped taking the grants of the folders above";
    if (event.Act === "FILED" || event.Act === "UNFILED") return `${words}: ${event.ObjectID ?? "a record"}`;
    if (event.Act === "CREATED") return `${words}: ${detail.name ?? ""}`;
    return words;
}

/** May `dragged` be dropped into `target` (null: the top level)? The server's own rule, asked before the drop so the tree
 *  only lights up where a move would be allowed: Manage on the folder moved and on where it goes (folder:create for the
 *  top), never inside itself, and never where it already is. The server still decides; this only spares a refusal. */
export function canDropFolder(folders: Row[], dragged: string | null, target: string | null, mayCreateTop: boolean) {
    const byId = new Map(folders.map(one => [one.USID, one]));
    const moving = dragged ? byId.get(dragged) : undefined;
    if (!moving || moving.Level !== "MANAGE") return false;
    if (target === null) return mayCreateTop && !!moving.ParentFolderUSID;
    const into = byId.get(target);
    if (!into || into.Level !== "MANAGE" || target === dragged || moving.ParentFolderUSID === target) return false;
    for (let at: Row | undefined = into; at; at = at.ParentFolderUSID ? byId.get(at.ParentFolderUSID) : undefined) {
        if (at.USID === dragged) return false;
    }
    return true;
}

/** May a record filed in `from` be dropped into `target`? FILE there, and not where it already is. The server also asks
 *  for a place in the record's owning position, which the screen cannot know, so a refusal of that is still possible. */
export function canDropRecord(folders: Row[], from: string | null | undefined, target: string) {
    const into = folders.find(one => one.USID === target);
    return !!from && !!into && (into.Level === "FILE" || into.Level === "MANAGE") && target !== from;
}

/** How long a drag must rest on a closed folder before it opens. */
export const HOVER_TO_EXPAND_MS = 600;

/** What the root of the tree is called: the place every top-level folder sits. */
export const ROOT_NAME = "All folders";

function FolderTree({folders, selected, onOpen, onNewChild, onMove, mayCreateTop, recordDrag, onDropRecord, onOpenRoot, onNewTop,
                     dragging, setDragging}: {
    folders: Row[]; selected?: string; onOpen: (usid: string) => void;
    /** Offered on every folder the caller may MANAGE, which is what making a subfolder takes. */
    onNewChild: (folder: Row) => void;
    /** A folder dropped on another (or on the top-level strip, as null). The page confirms before anything moves. */
    onMove: (folder: Row, into: Row | null) => void;
    mayCreateTop: boolean;
    /** A record being dragged from the open folder's list, with the folder it is filed in; null when none is. */
    recordDrag: Row | null;
    onDropRecord: (into: Row) => void;
    /** THE ROOT (Ed, 2026-11-03: "There needs to be a root folder that I can click on"): opens the top level, takes a
     *  folder dropped on it to the top, and offers a new top-level folder to whoever may make one. */
    onOpenRoot: () => void;
    onNewTop: () => void;
    /** The folder being dragged, held by the page so a folder can go from the tree to the content area and back. */
    dragging: string | null;
    setDragging: (usid: string | null) => void;
}) {
    const tree = useMemo(() => childrenOf(folders), [folders]);
    const [open, setOpen] = useState<Set<string>>(new Set());
    const [over, setOver] = useState<string | null>(null);
    const byId = useMemo(() => new Map(folders.map(one => [one.USID, one])), [folders]);
    // HOVER TO EXPAND: a drag resting on a closed folder opens it, so a folder can be dropped anywhere in the tree
    // without opening the way first. One timer, for the row the drag is over; leaving that row cancels it.
    const hover = useRef<{usid: string; timer: ReturnType<typeof setTimeout>} | null>(null);
    const cancelHover = (usid?: string) => {
        if (hover.current && (!usid || hover.current.usid === usid)) { clearTimeout(hover.current.timer); hover.current = null; }
    };
    const expandSoon = (usid: string) => {
        if (hover.current?.usid === usid) return;
        cancelHover();
        hover.current = {usid, timer: setTimeout(() => {
            hover.current = null;
            setOpen(current => new Set(current).add(usid));
        }, HOVER_TO_EXPAND_MS)};
    };
    useEffect(() => () => cancelHover(), []);
    const ends = () => { setDragging(null); setOver(null); cancelHover(); };
    const TOP = "__top__";

    // THE PATH TO THE OPEN FOLDER IS EXPANDED, so a folder reached by a link is shown where it sits.
    useEffect(() => {
        if (!selected) return;
        const byId = new Map(folders.map(one => [one.USID, one]));
        const next = new Set(open);
        let current = byId.get(selected)?.ParentFolderUSID;
        while (current && !next.has(current)) { next.add(current); current = byId.get(current)?.ParentFolderUSID; }
        if (next.size !== open.size) setOpen(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, folders]);

    const draw = (parent: string | null, depth: number): React.ReactNode =>
        (tree.get(parent) ?? []).map(one => {
            const kids = tree.get(one.USID) ?? [];
            const expanded = open.has(one.USID);
            const droppable = recordDrag ? canDropRecord(folders, recordDrag.FolderUSID, one.USID)
                                         : canDropFolder(folders, dragging, one.USID, mayCreateTop);
            return <React.Fragment key={one.USID}>
                <Box onClick={() => onOpen(one.USID)}
                     draggable={one.Level === "MANAGE"}
                     onDragStart={event => { event.dataTransfer.setData("text/plain", one.USID); event.dataTransfer.effectAllowed = "move"; setDragging(one.USID); }}
                     onDragEnd={ends}
                     onDragOver={event => {
                         if (kids.length && !expanded && (dragging || recordDrag)) expandSoon(one.USID);
                         if (droppable) { event.preventDefault(); event.dataTransfer.dropEffect = "move"; setOver(one.USID); }
                     }}
                     onDragLeave={event => {
                         // MOVING ONTO THE ROW'S OWN ICON OR NAME IS NOT LEAVING IT. dragleave bubbles from every child,
                         // and taking it at face value cancelled the expand and flickered the outline on every move.
                         if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
                         cancelHover(one.USID); setOver(current => current === one.USID ? null : current);
                     }}
                     onDrop={event => {
                         event.preventDefault();
                         const moving = dragging ? byId.get(dragging) : undefined;
                         ends();
                         if (!droppable) return;
                         if (recordDrag) onDropRecord(one);
                         else if (moving) onMove(moving, one);
                     }}
                     sx={{display: "flex", alignItems: "center", gap: "4px", pl: `${8 + depth * 16}px`, pr: 1, py: "5px",
                          outline: over === one.USID ? `2px dashed ${Blue3}` : "none", outlineOffset: "-2px",
                          opacity: dragging === one.USID ? 0.5 : 1,
                          cursor: "pointer", borderRadius: "4px", backgroundColor: one.USID === selected ? "#E8F4FA" : "transparent",
                          "&:hover": {backgroundColor: one.USID === selected ? "#E8F4FA" : "#F5F7FA"}}}>
                    <IconButton size="small" sx={{p: "2px", visibility: kids.length ? "visible" : "hidden"}}
                                aria-label={expanded ? "Collapse" : "Expand"}
                                onClick={event => {
                                    event.stopPropagation();
                                    const next = new Set(open);
                                    if (expanded) next.delete(one.USID); else next.add(one.USID);
                                    setOpen(next);
                                }}>
                        {expanded ? <ExpandMoreOutlined sx={{fontSize: 18}}/> : <ChevronRightOutlined sx={{fontSize: 18}}/>}
                    </IconButton>
                    {one.USID === selected ? <FolderOpenOutlined sx={{fontSize: 19, color: Blue3}}/> : <FolderOutlined sx={{fontSize: 19, color: Blue3}}/>}
                    <Typography sx={{fontSize: 13.5, color: Grey17, flexGrow: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                        {one.Name}
                    </Typography>
                    {!one.InheritGrants ? (
                        <Tooltip title="Does not take the grants of the folders above">
                            <LockPersonOutlined sx={{fontSize: 15, color: Grey5}}/>
                        </Tooltip>
                    ) : null}
                    <Typography sx={{fontSize: 11.5, color: Grey5}}>{one.RecordCount || ""}</Typography>
                    {one.Level === "MANAGE" ? (
                        <Tooltip title={`New folder inside “${one.Name}”`} placement="right">
                            <IconButton size="small" aria-label={`New folder inside ${one.Name}`} sx={{p: "2px", color: Grey6, "&:hover": {color: Blue3}}}
                                        onClick={event => { event.stopPropagation(); onNewChild(one); }}>
                                <CreateNewFolderOutlined sx={{fontSize: 16}}/>
                            </IconButton>
                        </Tooltip>
                    ) : <Box sx={{width: 20, flexShrink: 0}}/>}
                </Box>
                {expanded ? draw(one.USID, depth + 1) : null}
            </React.Fragment>;
        });

    const toTop = !recordDrag && canDropFolder(folders, dragging, null, mayCreateTop);
    return <Box>
        <Box onClick={onOpenRoot}
             onDragOver={event => { if (toTop) { event.preventDefault(); event.dataTransfer.dropEffect = "move"; setOver(TOP); } }}
             onDragLeave={event => { if (event.currentTarget.contains(event.relatedTarget as Node | null)) return; setOver(current => current === TOP ? null : current); }}
             onDrop={event => {
                 event.preventDefault();
                 const moving = dragging ? byId.get(dragging) : undefined;
                 ends();
                 if (moving && toTop) onMove(moving, null);
             }}
             sx={{display: "flex", alignItems: "center", gap: "6px", pl: "8px", pr: 1, py: "5px", cursor: "pointer", borderRadius: "4px",
                  outline: over === TOP ? `2px dashed ${Blue3}` : "none", outlineOffset: "-2px",
                  backgroundColor: !selected ? "#E8F4FA" : "transparent", "&:hover": {backgroundColor: !selected ? "#E8F4FA" : "#F5F7FA"}}}>
            <FolderSpecialOutlined sx={{fontSize: 19, color: Blue3}}/>
            <Typography sx={{fontSize: 13.5, color: Grey17, fontWeight: 500, flexGrow: 1}}>{ROOT_NAME}</Typography>
            {mayCreateTop ? (
                <Tooltip title="New folder at the top level" placement="right">
                    <IconButton size="small" aria-label="New folder at the top level" sx={{p: "2px", color: Grey6, "&:hover": {color: Blue3}}}
                                onClick={event => { event.stopPropagation(); onNewTop(); }}>
                        <CreateNewFolderOutlined sx={{fontSize: 16}}/>
                    </IconButton>
                </Tooltip>
            ) : null}
        </Box>
        {draw(null, 1)}
    </Box>;
}

/** Where a match was found, as a reader would say it. */
export function matchPlace(match: Row) {
    if (match.Source === "Record") return "Name or description";
    if (match.Source === "Form") return `Form: ${humanField(match.Label)}`;
    if (match.Source === "Document") return `Parkour AI Doc: ${match.Label}`;
    return `File: ${match.Label}`;
}

/** A form field's code as words: "dueDate" and "due_date" both read "Due date". */
export function humanField(code: string | null | undefined) {
    const words = String(code ?? "").replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ").trim().toLowerCase();
    return words ? words[0].toUpperCase() + words.slice(1) : "a field";
}

/** A snippet cut at the server's markers into plain and matched runs, so the matched words are bold and nothing in the
 *  text itself is ever read as markup. */
export function snippetRuns(snippet: string, start: string, end: string): {text: string; hit: boolean}[] {
    const out: {text: string; hit: boolean}[] = [];
    let rest = snippet ?? "";
    while (rest) {
        const at = rest.indexOf(start);
        if (at < 0) { out.push({text: rest, hit: false}); break; }
        if (at > 0) out.push({text: rest.slice(0, at), hit: false});
        const close = rest.indexOf(end, at + start.length);
        if (close < 0) { out.push({text: rest.slice(at + start.length), hit: true}); break; }
        out.push({text: rest.slice(at + start.length, close), hit: true});
        rest = rest.slice(close + end.length);
    }
    return out;
}

/** What the results heading says: how many, matching what, and where. */
export function resultsTitle(result: Row, scope: string) {
    const many = `${result.Total} record${result.Total === 1 ? "" : "s"}`;
    return result.Query ? `${many} match “${result.Query}” ${scope}` : `${many} ${scope}`;
}

function SearchResults({search, scope, onOpenFolder, onClose, closeLabel, onWiden}: {
    search: {loading: boolean; result?: Row; problem?: string};
    /** "in “Procedures” and its subfolders", or "in every folder you can see". */
    scope: string;
    onOpenFolder: (usid: string) => void;
    onClose?: () => void; closeLabel?: string;
    /** Offered when a folder narrows the scope: search every folder the reader can see instead. */
    onWiden?: () => void;
}) {
    const result = search.result;
    return (
        <Box>
            <Box sx={{display: "flex", alignItems: "center", gap: 1.5, pb: 1.5, borderBottom: "1px solid #E4E7EC"}}>
                <SearchOutlined sx={{fontSize: 24, color: Blue3}}/>
                <Typography sx={{fontSize: 17, fontWeight: 500, color: Grey17, flexGrow: 1}}>
                    {result ? resultsTitle(result, scope) : search.loading ? `Finding records ${scope}…` : "The search did not work"}
                </Typography>
                {onWiden ? <Button size="small" sx={{textTransform: "none"}} onClick={onWiden}>Every folder</Button> : null}
                {onClose ? <Button size="small" startIcon={<CloseOutlined/>} sx={{textTransform: "none", color: Grey7}} onClick={onClose}>{closeLabel ?? "Back to the folder"}</Button> : null}
            </Box>
            {search.loading && !result ? <CircularProgress size={22} sx={{mt: 3}}/> : null}
            {search.problem ? <Typography sx={{fontSize: 13, color: "#B3261E", mt: 2}}>{search.problem}</Typography> : null}
            {result ? (
                <>
                    <Typography sx={{fontSize: 12.5, color: Grey5, mt: 1}}>
                        Out of {result.RecordsSearched} record{result.RecordsSearched === 1 ? "" : "s"} your view can open, in {result.FoldersSearched} folder{result.FoldersSearched === 1 ? "" : "s"}
                        {result.Query ? ": searched by their names and descriptions, form answers, Parkour AI Docs, and the text of their files." : "."}
                    </Typography>
                    {(result.Results ?? []).map((one: Row) => (
                        <Box key={one.RevisionUSID} sx={{py: 1.5, borderBottom: "1px solid #F2F4F7"}}>
                            <Box sx={{display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap"}}>
                                <ObjectIdLink id={one.ObjectID} revisionUSID={one.RevisionUSID} size={13.5}/>
                                <Typography sx={{fontSize: 13.5, color: Grey17}}>{one.Name}</Typography>
                                <Chip size="small" label={classificationByCode(one.Classification)?.singular ?? one.Classification} sx={{height: 20, fontSize: 11}}/>
                                {one.Status ? <Typography sx={{fontSize: 11.5, color: Grey7}}>{one.Status}</Typography> : null}
                                <Typography sx={{fontSize: 12.5, color: Grey5}}>in</Typography>
                                <Typography sx={{fontSize: 12.5, color: Blue3, cursor: "pointer"}} onClick={() => onOpenFolder(one.FolderUSID)}>{one.Folder}</Typography>
                            </Box>
                            {(one.Matches ?? []).map((match: Row, index: number) => (
                                <Box key={index} sx={{display: "flex", gap: 1.5, mt: 0.75, pl: 0.25}}>
                                    <Typography sx={{fontSize: 12, color: Grey5, width: 190, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                                        {matchPlace(match)}
                                    </Typography>
                                    <Typography sx={{fontSize: 12.5, color: Grey7, lineHeight: 1.5}}>
                                        {snippetRuns(match.Snippet, result.MatchStart, result.MatchEnd).map((run, at) => run.hit
                                            ? <b key={at} style={{color: Grey17}}>{run.text}</b> : <React.Fragment key={at}>{run.text}</React.Fragment>)}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    ))}
                    {result.Truncated ? (
                        <Typography sx={{fontSize: 12.5, color: Grey5, mt: 1.5}}>Showing the best {(result.Results ?? []).length} of {result.Total}. Add a word to narrow it.</Typography>
                    ) : null}
                    {result.FilesNotRead ? (
                        <Typography sx={{fontSize: 12.5, color: Grey5, mt: 1.5}}>
                            {result.FilesNotRead} file{result.FilesNotRead === 1 ? " here has" : "s here have"} no readable text, so {result.FilesNotRead === 1 ? "it was" : "they were"} searched by name only.
                        </Typography>
                    ) : null}
                </>
            ) : null}
        </Box>
    );
}

/** A move hands the folder, and every record in it, to the grants of its new place -- so it is asked, not done on drop. */
function MoveDialog({moving, api, onCancel, onMoved}: {
    moving: {folder: Row; into: Row | null}; api: any; onCancel: () => void; onMoved: () => void;
}) {
    const [busy, setBusy] = useState(false);
    const [refusal, setRefusal] = useState<string | null>(null);
    const where = moving.into ? `inside “${moving.into.Name}”` : "to the top level";
    return <Dialog open onClose={onCancel} maxWidth="xs" fullWidth>
        <DialogTitle sx={{fontSize: 17}}>Move “{moving.folder.Name}” {where}?</DialogTitle>
        <DialogContent>
            <Typography sx={{fontSize: 13.5, color: Grey7, lineHeight: 1.6}}>
                {moving.folder.InheritGrants
                    ? `Its records take the access of their new place: whoever is given the folders above it there will see them, and whoever saw them only through the folders above it now will not.`
                    : `It does not take the grants of the folders above it, so who can see its records does not change.`}
                {" "}Its own grants and its subfolders move with it.
            </Typography>
            {refusal ? <Typography sx={{fontSize: 12.5, color: "#B3261E", mt: 1.5}}>{refusal}</Typography> : null}
        </DialogContent>
        <DialogActions>
            <Button onClick={onCancel} sx={{textTransform: "none"}}>Cancel</Button>
            <Button variant="contained" disabled={busy} sx={{textTransform: "none"}}
                    onClick={() => {
                        setBusy(true); setRefusal(null);
                        api.put("t1/v1/folders", {USID: moving.folder.USID, ...(moving.into ? {ParentFolderUSID: moving.into.USID} : {MoveToTop: true})})
                            .then(onMoved)
                            .catch((problem: any) => { setBusy(false); setRefusal(refusalOf(problem, "The move did not work.")); });
                    }}>
                Move
            </Button>
        </DialogActions>
    </Dialog>;
}

/** Moving a record to another folder changes who sees it through a folder, so it is asked, not done on drop. */
function RefileDialog({refiling, api, onCancel, onMoved}: {
    refiling: {record: Row; from: Row; into: Row}; api: any; onCancel: () => void; onMoved: () => void;
}) {
    const [busy, setBusy] = useState(false);
    const [refusal, setRefusal] = useState<string | null>(null);
    const {record, from, into} = refiling;
    const label = record.ObjectID ?? record.Name;
    return <Dialog open onClose={onCancel} maxWidth="xs" fullWidth>
        <DialogTitle sx={{fontSize: 17}}>Move {label} to “{into.Name}”?</DialogTitle>
        <DialogContent>
            <Typography sx={{fontSize: 13.5, color: Grey7, lineHeight: 1.6}}>
                It will be seen by whoever is given “{into.Name}” and the folders above it, and no longer by those who saw it only
                through “{from.Name}”. Shares made by hand on the record do not change.
            </Typography>
            {refusal ? <Typography sx={{fontSize: 12.5, color: "#B3261E", mt: 1.5}}>{refusal}</Typography> : null}
        </DialogContent>
        <DialogActions>
            <Button onClick={onCancel} sx={{textTransform: "none"}}>Cancel</Button>
            <Button variant="contained" disabled={busy} sx={{textTransform: "none"}}
                    onClick={() => {
                        setBusy(true); setRefusal(null);
                        api.post("t1/v1/folder_records", {FolderUSID: into.USID, ObjectRevisionUSID: record.RevisionUSID})
                            .then(onMoved)
                            .catch((problem: any) => { setBusy(false); setRefusal(refusalOf(problem, "The move did not work.")); });
                    }}>
                Move
            </Button>
        </DialogActions>
    </Dialog>;
}

function NameDialog({title, initial, describe, onCancel, onSave}: {
    title: string; initial?: Row; describe?: boolean; onCancel: () => void;
    onSave: (name: string, description: string) => Promise<void>;
}) {
    const [name, setName] = useState(initial?.Name ?? "");
    const [description, setDescription] = useState(initial?.Description ?? "");
    const [busy, setBusy] = useState(false);
    const [refusal, setRefusal] = useState<string | null>(null);
    return <Dialog open onClose={onCancel} fullWidth maxWidth="xs">
        <DialogTitle sx={{fontSize: 16}}>{title}</DialogTitle>
        <DialogContent sx={{display: "flex", flexDirection: "column", gap: 2, pt: "8px !important"}}>
            <TextField size="small" label="Name" value={name} autoFocus onChange={event => setName(event.target.value)}/>
            {describe ? <TextField size="small" label="Description" value={description} multiline minRows={2}
                                   onChange={event => setDescription(event.target.value)}/> : null}
            {refusal ? <Typography sx={{fontSize: 12.5, color: "#B3261E"}}>{refusal}</Typography> : null}
        </DialogContent>
        <DialogActions>
            <Button onClick={onCancel} sx={{textTransform: "none"}}>Cancel</Button>
            <Button variant="contained" disabled={busy || !name.trim()} sx={{textTransform: "none"}}
                    onClick={() => {
                        setBusy(true); setRefusal(null);
                        onSave(name.trim(), description).catch(problem => setRefusal(refusalOf(problem, "That did not work, and the reason did not come back.")))
                            .finally(() => setBusy(false));
                    }}>Save</Button>
        </DialogActions>
    </Dialog>;
}

function AccessDialog({folder, api, onClose, onChanged}: {folder: Row; api: any; onClose: () => void; onChanged: (folder: Row) => void}) {
    const [grantees, setGrantees] = useState<Record<string, Row[]>>({});
    const [kind, setKind] = useState("Position");
    const [whom, setWhom] = useState("");
    const [level, setLevel] = useState("VIEW");
    const [busy, setBusy] = useState(false);
    const [refusal, setRefusal] = useState<string | null>(null);

    useEffect(() => {
        api.get("t1/v1/folder_grants", {params: {FolderUSID: folder.USID}})
            .then((answer: any) => setGrantees(answer?.data?.Grantees ?? {}))
            .catch((problem: any) => setRefusal(refusalOf(problem, "Who this folder could be given to did not load.")));
    }, [api, folder.USID]);

    const act = (request: Promise<any>) => {
        setBusy(true); setRefusal(null);
        request.then((answer: any) => { onChanged(answer.data); setWhom(""); })
            .catch((problem: any) => setRefusal(refusalOf(problem, "That did not work, and the reason did not come back.")))
            .finally(() => setBusy(false));
    };

    const access: Row[] = folder.Access ?? [];
    return <Dialog open onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle sx={{fontSize: 16}}>Who has “{folder.Name}”</DialogTitle>
        <DialogContent sx={{display: "flex", flexDirection: "column", gap: 2}}>
            <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6}}>
                Everyone listed can see every record filed in this folder and the folders inside it, read-only. Giving a
                folder never lets anybody change a record: that still comes from owning it or from a share made on the
                record itself.
            </Typography>
            <FormControlLabel
                control={<Switch size="small" checked={!!folder.InheritGrants} disabled={busy}
                                 onChange={event => act(api.put("t1/v1/folders", {USID: folder.USID, InheritGrants: event.target.checked}))}/>}
                label={<Typography sx={{fontSize: 13}}>Take the grants of the folders above</Typography>}/>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {["Given to", "Kind", "Level", "From", ""].map(one => <TableCell key={one} sx={{fontSize: 12, color: Grey5}}>{one}</TableCell>)}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {access.map(one => (
                        <TableRow key={`${one.GranteeType}:${one.GranteeUSID}`}>
                            <TableCell sx={{fontSize: 13}}>{one.Name}</TableCell>
                            <TableCell sx={{fontSize: 13, color: Grey7}}>{one.GranteeType}</TableCell>
                            <TableCell sx={{fontSize: 13}}>{LEVEL_WORDS[one.Level] ?? one.Level}</TableCell>
                            <TableCell sx={{fontSize: 13, color: Grey7}}>{one.Source === "Owner" ? "Owns the folder" : one.Source}</TableCell>
                            <TableCell align="right">
                                {one.Source === "This folder" ? (
                                    <Button size="small" disabled={busy} sx={{textTransform: "none"}}
                                            onClick={() => act(api.delete("t1/v1/folder_grants", {params: {
                                                FolderUSID: folder.USID, GranteeType: one.GranteeType, GranteeUSID: one.GranteeUSID}}))}>
                                        Take away
                                    </Button>
                                ) : null}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <Box sx={{display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap"}}>
                <TextField select size="small" label="Kind" value={kind} sx={{width: 150}}
                           onChange={event => { setKind(event.target.value); setWhom(""); }}>
                    {GRANTEE_TYPES.map(one => <MenuItem key={one} value={one}>{one}</MenuItem>)}
                </TextField>
                <TextField select size="small" label={kind} value={whom} sx={{minWidth: 260, flexGrow: 1}}
                           onChange={event => setWhom(event.target.value)}>
                    {(grantees[kind] ?? []).map(one => <MenuItem key={one.USID} value={one.USID}>{one.Name}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Level" value={level} sx={{width: 130}}
                           onChange={event => setLevel(event.target.value)}>
                    {Object.keys(LEVEL_WORDS).map(one => (
                        <MenuItem key={one} value={one}>
                            <Tooltip title={LEVEL_MEANS[one]} placement="right"><span>{LEVEL_WORDS[one]}</span></Tooltip>
                        </MenuItem>
                    ))}
                </TextField>
                <Button variant="contained" disabled={busy || !whom} sx={{textTransform: "none"}}
                        onClick={() => act(api.post("t1/v1/folder_grants", {FolderUSID: folder.USID, GranteeType: kind, GranteeUSID: whom, Level: level}))}>
                    Give
                </Button>
            </Box>
            <Typography sx={{fontSize: 12, color: Grey5}}>{LEVEL_MEANS[level]}.</Typography>
            {refusal ? <Typography sx={{fontSize: 12.5, color: "#B3261E"}}>{refusal}</Typography> : null}
        </DialogContent>
        <DialogActions><Button onClick={onClose} sx={{textTransform: "none"}}>Done</Button></DialogActions>
    </Dialog>;
}

export default function FoldersPage() {
    const appContext: any = useContext(AppContext);
    const api = useMemo(() => makeCresAxios(appContext?.sessionContext),
                        // eslint-disable-next-line react-hooks/exhaustive-deps
                        [appContext?.sessionContext]);
    const navigate = useNavigate();
    const {usid} = useParams();

    const [folders, setFolders] = useState<Row[]>([]);
    const [mayCreateTop, setMayCreateTop] = useState(false);
    const [folder, setFolder] = useState<Row | null>(null);
    const [missing, setMissing] = useState<string | null>(null);
    const [tab, setTab] = useState(0);
    const [history, setHistory] = useState<Row[] | null>(null);
    const [dialog, setDialog] = useState<null | "new" | "child" | "rename" | "access">(null);
    const [refusal, setRefusal] = useState<string | null>(null);
    const [drawer, setDrawer] = useState<string | null>(null);
    // THE FOLDER A NEW ONE GOES INSIDE: the open folder from its header, or any row of the tree from its own icon.
    const [parentFor, setParentFor] = useState<Row | null>(null);
    const [query, setQuery] = useState("");
    const [search, setSearch] = useState<null | {loading: boolean; result?: Row; problem?: string}>(null);
    // FOLDERS OR FILTER BY (Ed, 2026-11-03): the left pane shows the tree, or the filters over the open folder and
    // below -- every folder the reader can see when none is open. The words in the search box apply in both.
    const [mode, setMode] = useState<"folders" | "filter">("folders");
    // THE LEFT PANE WIDENS (Ed, 2026-11-03): the record lists' own gripper, so it drags, collapses and remembers its
    // width the same way. Folder names and filter values are the tenant's, and their length is not ours to choose.
    const pane = useResizablePanel({storageKey: "folders.leftPane", defaultWidth: 300, minWidth: 220, maxWidth: 620, collapseAt: 160});
    const [filters, setFilters] = useState<FolderFilters>(NO_FILTERS);
    const [committed, setCommitted] = useState("");
    const [viewTick, setViewTick] = useState(0);
    const asked = useRef(0);
    const sessionContext = appContext?.sessionContext;
    const [activeMode, setActiveMode] = useState<string>(sessionContext?.getActiveVisibility()?.Mode ?? "");
    const modes = availableModes(activeMode, sessionContext?.getPrivileges?.() ?? [], sessionContext?.getAccount?.());

    // WHAT IS IN FLIGHT, counted, so the bar stays up until the LAST read lands rather than the first: a view change
    // reloads the tree and the folder together, and either may be the slow one.
    const [pending, setPending] = useState(0);
    const track = useCallback(<T,>(request: Promise<T>): Promise<T> => {
        setPending(count => count + 1);
        return request.finally(() => setPending(count => count - 1));
    }, []);
    // NOT "NO FOLDERS" UNTIL THE TREE HAS ANSWERED: an empty list before the first read is not knowledge.
    const [treeLoaded, setTreeLoaded] = useState(false);

    // A FAILED READ IS NOT AN EMPTY TREE. The first read after sign-in is often answered 401 while the token refreshes,
    // and the refreshed session reads again; saying "no folder has been given to you" in that gap was a false statement.
    // So a 401 keeps the spinner, and any other failure says the folders could not be read -- never that there are none.
    const [treeProblem, setTreeProblem] = useState<string | null>(null);
    const loadTree = useCallback(() => {
        track(api.get("t1/v1/folders"))
            .then((answer: any) => {
                setFolders(answer?.data?.Folders ?? []); setMayCreateTop(!!answer?.data?.MayCreateTopLevel);
                setTreeProblem(null); setTreeLoaded(true);
            })
            .catch((problem: any) => {
                if (problem?.response?.status === 401) return;
                setTreeProblem(refusalOf(problem, "The folders could not be read. Reload the page to try again."));
            });
    }, [api, track]);

    const loadFolder = useCallback(() => {
        setRefusal(null); setHistory(null);
        if (!usid) { setFolder(null); setMissing(null); return; }
        track(api.get("t1/v1/folders", {params: {USID: usid}}))
            .then((answer: any) => { setFolder(answer.data); setMissing(null); })
            .catch((problem: any) => {
                if (problem?.response?.status === 401) return;      // the refreshed session reads it again; see loadTree
                setFolder(null); setMissing(refusalOf(problem, "That folder could not be opened."));
            });
    }, [api, usid, track]);

    useEffect(() => { loadTree(); }, [loadTree]);
    useEffect(() => { loadFolder(); setTab(0); }, [loadFolder]);
    // A FOLDER OPENED FROM THE TREE starts without the last folder's search; under Filter by the scope simply moves.
    useEffect(() => { if (mode === "folders") { setCommitted(""); setQuery(""); } }, [usid, mode]);

    const runSearch = () => setCommitted(query.trim());
    const filtersKey = JSON.stringify(filters);
    // ONE READ ANSWERS BOTH PANES: the records and every filter's counts. Under Folders it runs only for words; under
    // Filter by it always runs. Typed bounds wait a moment, and an answer to an older question is dropped on arrival.
    useEffect(() => {
        if (mode === "folders" && !committed) { setSearch(null); return; }
        const ask = ++asked.current;
        setSearch(current => ({loading: true, result: current?.result}));
        const timer = setTimeout(() => {
            const params: Row = {View: "search", ...(usid ? {USID: usid} : {}), ...(committed ? {Query: committed} : {})};
            if (mode === "filter" && filtering(filters)) params.Filters = JSON.stringify(filtersPayload(filters));
            track(api.get("t1/v1/folders", {params}))
                .then((answer: any) => { if (ask === asked.current) setSearch({loading: false, result: answer.data}); })
                .catch((problem: any) => { if (ask === asked.current) setSearch({loading: false, problem: refusalOf(problem, "The search did not work.")}); });
        }, 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, usid, committed, filtersKey, viewTick, api, track]);
    // FINDER'S FOUR VIEWS of what the open folder (or the root) holds, the last one chosen kept in this browser.
    const [view, setView] = useState<FolderView>(rememberedView);
    const chooseView = (next: FolderView) => { setView(next); rememberView(next); };
    const tree = useMemo(() => childrenOf(folders), [folders]);
    const folderRows = useMemo(() => new Map(folders.map(one => [one.USID, one])), [folders]);
    // THE TREE'S OWN ROWS for a folder's subfolders, because they carry what the tiles show (records, level, lock).
    const subfolders = useCallback((parent: string | null) => tree.get(parent) ?? [], [tree]);
    const contents = useMemo(() => usid && folder
        ? itemsOf((folder.Children ?? []).map((one: Row) => folderRows.get(one.USID) ?? one), folder.Records ?? [])
        : itemsOf(subfolders(null), []), [usid, folder, folderRows, subfolders]);
    // COLUMNS IS THE PATH: the root, each folder above the open one, and the open one. An ancestor's records are read
    // only when this view is showing, once each, and forgotten whenever the tree is read again.
    const [ancestors, setAncestors] = useState<Record<string, Row[]>>({});
    useEffect(() => { setAncestors({}); }, [folders]);
    const path: Row[] = useMemo(() => (usid && folder ? [...(folder.Path ?? []), {USID: folder.USID, Name: folder.Name}] : []), [usid, folder]);
    useEffect(() => {
        if (view !== "columns") return;
        path.slice(0, -1).filter(one => !(one.USID in ancestors)).forEach(one => {
            track(api.get("t1/v1/folders", {params: {USID: one.USID}}))
                .then((answer: any) => setAncestors(current => ({...current, [one.USID]: answer?.data?.Records ?? []})))
                .catch(() => setAncestors(current => ({...current, [one.USID]: []})));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, path]);
    const columns: Column[] = useMemo(() => [
        {usid: null, name: ROOT_NAME, folders: subfolders(path.length ? (folderRows.get(path[0].USID)?.ParentFolderUSID ?? null) : null), records: [],
         next: path[0]?.USID ?? null},
        ...path.map((one, index) => ({
            usid: one.USID, name: one.Name, folders: subfolders(one.USID),
            records: index === path.length - 1 ? (folder?.Records ?? []) : (ancestors[one.USID] ?? null),
            next: path[index + 1]?.USID ?? null,
        })),
    ], [path, subfolders, folderRows, folder, ancestors]);
    const scope = folder && usid ? `in “${folder.Name}” and its subfolders` : "in every folder you can see";
    const newChild = (parent: Row) => { setParentFor(parent); setDialog("child"); };
    const [moving, setMoving] = useState<null | {folder: Row; into: Row | null}>(null);
    const [recordDrag, setRecordDrag] = useState<Row | null>(null);
    const [folderDrag, setFolderDrag] = useState<string | null>(null);
    const [refiling, setRefiling] = useState<null | {record: Row; from: Row; into: Row}>(null);
    const mayFile = folder?.Level === "FILE" || folder?.Level === "MANAGE";
    // A DROP ON A FOLDER IN THE CONTENT AREA, by the same rules as the tree and confirmed by the same dialogs.
    const contentDrop = {
        folderDrag, onFolderDragStart: (one: Row) => setFolderDrag(one.USID),
        onDragFinished: () => { setFolderDrag(null); setRecordDrag(null); },
        canDrop: (target: Row) => recordDrag ? canDropRecord(folders, recordDrag.FolderUSID, target.USID)
                                             : canDropFolder(folders, folderDrag, target.USID, mayCreateTop),
        onDrop: (target: Row) => {
            if (recordDrag && folder) setRefiling({record: recordDrag, from: folder, into: target});
            else if (folderDrag) { const moving = folderRows.get(folderDrag); if (moving) setMoving({folder: moving, into: target}); }
            setFolderDrag(null); setRecordDrag(null);
        },
    };
    // ANOTHER VIEW, ANOTHER TREE: the axios client reads the active persona per request, so a reload is all it takes.
    useEffect(() => {
        const onChange = () => {
            setActiveMode(sessionContext?.getActiveVisibility()?.Mode ?? "");
            loadTree(); loadFolder(); setViewTick(tick => tick + 1);
        };
        EventBus.visibility.addListener("change-visibility", onChange);
        return () => { EventBus.visibility.removeListener("change-visibility", onChange); };
    }, [sessionContext, loadTree, loadFolder]);

    const selectMode = (mode: string) => {
        sessionContext?.setActiveVisibility(mode, sessionContext?.getActiveVisibility()?.Value ?? "");
        EventBus.visibility.emit("change-visibility", sessionContext?.getActiveVisibility());
    };
    useEffect(() => {
        if (tab !== 1 || !folder || history) return;
        track(api.get("t1/v1/folders", {params: {USID: folder.USID, View: "history"}}))
            .then((answer: any) => setHistory(answer?.data?.Events ?? []))
            .catch(() => setHistory([]));
    }, [api, tab, folder, history, track]);

    const changed = (next: Row) => { setFolder(next); loadTree(); };
    const manage = folder?.Level === "MANAGE";

    return (
        <div style={{display: "flex", flexDirection: "column", flexGrow: 1, height: "100%", backgroundColor: White, minHeight: 0}}>
            <PageHeader title="Folders"><PersonaHeaderInfo/></PageHeader>
            {/* THE RECORD LISTS' HEADER (Ed, 2026-11-03): a tinted icon tile and the name, then the view beneath it,
                with the search at the far end of the same band -- the view decides what the search can reach. */}
            <Box sx={{px: "30px", flexShrink: 0}}>
                <Box sx={{display: "flex", alignItems: "center", gap: 1.5, py: 1.5, borderBottom: "1px solid #E4E7EC"}}>
                    <Box sx={{width: 34, height: 34, borderRadius: "6px", backgroundColor: "rgba(20,152,216,0.12)",
                              display: "flex", alignItems: "center", justifyContent: "center"}}>
                        <FolderOutlined sx={{color: Blue3, fontSize: 20}}/>
                    </Box>
                    <Typography sx={{fontSize: 20, fontWeight: 500, color: Grey17}}>Folders</Typography>
                </Box>
                <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: "12px",
                          borderBottom: "1px solid #E4E7EC", mt: 2}}>
                    <Box sx={{display: "flex", gap: "22px"}}>
                    {modes.map(one => (
                        <Typography key={one.mode} onClick={() => selectMode(one.mode)}
                                    sx={{cursor: "pointer", pb: "6px", fontSize: 14, fontWeight: activeMode === one.mode ? 600 : 400,
                                         color: activeMode === one.mode ? Grey17 : Grey6,
                                         borderBottom: activeMode === one.mode ? `2px solid ${Blue3}` : "2px solid transparent"}}>
                            {one.label}
                        </Typography>
                    ))}
                    </Box>
                    <TextField size="small" value={query} onChange={event => setQuery(event.target.value)}
                               onKeyDown={event => { if (event.key === "Enter") runSearch(); }}
                               placeholder={mode === "filter" ? (folder && usid ? `Search “${folder.Name}” and its subfolders` : "Search every folder you can see")
                                   : folder && usid ? `Search “${folder.Name}” and its subfolders` : "Search every folder you can see"}
                               sx={{width: 380, mb: "4px", "& .MuiInputBase-input": {fontSize: 13.5}}}
                               slotProps={{input: {
                                   startAdornment: <InputAdornment position="start"><SearchOutlined sx={{fontSize: 18, color: Grey5}}/></InputAdornment>,
                                   endAdornment: query ? (
                                       <InputAdornment position="end">
                                           <IconButton size="small" aria-label="Clear the search" onClick={() => { setQuery(""); setCommitted(""); }}>
                                               <CloseOutlined sx={{fontSize: 16}}/>
                                           </IconButton>
                                       </InputAdornment>
                                   ) : null,
                               }}}/>
                </Box>
            </Box>
            {/* THE FETCH INDICATOR: a thin bar across both panes while anything is being read. Its space is always kept, so
                the panes do not jump as it comes and goes. */}
            <Box sx={{height: 3, flexShrink: 0}}>
                {pending > 0 ? <LinearProgress aria-label="Loading" sx={{height: 3}}/> : null}
            </Box>
            <RecordDrawerContext.Provider value={setDrawer}>
            <div style={{display: "flex", flexDirection: "row", flexGrow: 1, minHeight: 0}}>
                <Box ref={pane.panelRef} sx={{display: "flex", flexDirection: "row", flexShrink: 0, minHeight: 0}}>
                {!pane.collapsed && (
                <Box sx={{width: pane.width, flexShrink: 0, borderRight: "1px solid #E4E7EC", overflow: "auto", p: 1.5}}>
                    <Tabs value={mode} onChange={(_, next) => setMode(next)} variant="fullWidth"
                          sx={{mb: 1.5, minHeight: 34, borderBottom: "1px solid #E4E7EC", "& .MuiTab-root": {minHeight: 34, textTransform: "none", fontSize: 13.5}}}>
                        <Tab value="folders" label="Folders"/>
                        <Tab value="filter" label="Filter by"/>
                    </Tabs>
                    {mode === "filter" ? (
                        <FolderFilterPane answer={search?.result ?? null} filters={filters}
                                          onToggle={(key, value) => setFilters(current => toggled(current, key, value))}
                                          onRange={(key, bound, value) => setFilters(current => ({
                                              ...current, ranges: {...current.ranges, [key]: {...(current.ranges[key] ?? {from: "", to: ""}), [bound]: value}},
                                          }))}
                                          onClear={() => setFilters(NO_FILTERS)}/>
                    ) : <>
                    {treeProblem ? <Typography sx={{fontSize: 13, color: "#B3261E", p: 1, lineHeight: 1.6}}>{treeProblem}</Typography>
                        : !treeLoaded ? <CircularProgress size={20} sx={{m: 1.5}} aria-label="Loading the folders"/> : folders.length || mayCreateTop ? <FolderTree folders={folders} selected={usid} onOpen={one => navigate(`/folders/${one}`)} onNewChild={newChild}
                                                     onMove={(one, into) => setMoving({folder: one, into})} mayCreateTop={mayCreateTop}
                                                     onOpenRoot={() => navigate("/folders")} onNewTop={() => { setParentFor(null); setDialog("new"); }}
                                                     dragging={folderDrag} setDragging={setFolderDrag}
                                                     recordDrag={recordDrag}
                                                     onDropRecord={into => { if (recordDrag && folder) setRefiling({record: recordDrag, from: folder, into}); setRecordDrag(null); }}/> : (
                        <Typography sx={{fontSize: 13, color: Grey5, p: 1, lineHeight: 1.6}}>
                            No folder has been given to you yet{mayCreateTop ? ", and none has been made." : "."}
                        </Typography>
                    )}
                    </>}
                </Box>
                )}
                {/* A PANE CLOSED WHILE FILTERS ARE ON leaves a list that is a subset with nothing saying so; the dot says so. */}
                <ResizeGripper collapsed={pane.collapsed} onMouseDown={pane.onGripperDown} setCollapsed={pane.setCollapsed}
                               openLabel={mode === "filter" && filtering(filters) ? "Open folders and filters (filters are active)" : "Open folders and filters"}
                               marker={pane.collapsed && mode === "filter" && filtering(filters)}/>
                </Box>

                <Box sx={{flex: 1, overflow: "auto", px: 4, py: 2.5, minWidth: 0}}>
                    {mode === "filter" ? (
                        <SearchResults search={search ?? {loading: true}} scope={scope}
                                       onOpenFolder={one => { setMode("folders"); navigate(`/folders/${one}`); }}
                                       onWiden={usid ? () => navigate("/folders") : undefined}
                                       onClose={() => setMode("folders")} closeLabel="Back to the folders"/>
                    ) : search ? (
                        <SearchResults search={search} scope={scope} onOpenFolder={one => navigate(`/folders/${one}`)}
                                       onClose={() => { setQuery(""); setCommitted(""); }}/>
                    ) : !usid ? (
                        // THE ROOT: every top-level folder this view can see. It holds folders only -- a record always
                        // lives in a folder -- and has no trail of its own.
                        <>
                            <Box sx={{display: "flex", alignItems: "center", gap: 1.5, pb: 1.5, borderBottom: "1px solid #E4E7EC"}}>
                                <FolderSpecialOutlined sx={{fontSize: 26, color: Blue3}}/>
                                <Typography sx={{fontSize: 20, fontWeight: 500, color: Grey17, flexGrow: 1}}>{ROOT_NAME}</Typography>
                                {mayCreateTop ? (
                                    <Button size="small" startIcon={<CreateNewFolderOutlined/>} sx={{textTransform: "none"}}
                                            onClick={() => { setParentFor(null); setDialog("new"); }}>New folder</Button>
                                ) : null}
                                <ViewSwitch view={view} onView={chooseView}/>
                            </Box>
                            <Typography sx={{fontSize: 13, color: Grey7, mt: 1.25}}>
                                A record lives in one folder, and whoever a folder is given to can see what is filed in it.
                            </Typography>
                            {treeProblem ? <Typography sx={{fontSize: 13, color: "#B3261E", mt: 1.5}}>{treeProblem}</Typography>
                                : !treeLoaded ? <CircularProgress size={22} sx={{mt: 3}} aria-label="Loading the folders"/> : treeLoaded ? (
                                <FolderContents view={view} items={contents} columns={columns} drop={contentDrop}
                                                empty={`No folder has been given to you yet${mayCreateTop ? ", and none has been made." : "."}`}
                                                onOpenFolder={one => navigate(`/folders/${one}`)} onOpenRecord={setDrawer}
                                                mayFile={false} onRecordDragStart={() => undefined} onRecordDragEnd={() => undefined}/>
                            ) : null}
                        </>
                    ) : missing ? (
                        <Typography sx={{fontSize: 14, color: Grey7, mt: 2}}>{missing}</Typography>
                    ) : !folder ? <CircularProgress size={22} sx={{mt: 3}} aria-label="Loading the folder"/> : (
                        <>
                            <Box sx={{display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", mb: 0.5}}>
                                {(folder.Path ?? []).map((one: Row) => (
                                    <React.Fragment key={one.USID}>
                                        <Typography sx={{fontSize: 12.5, color: Blue3, cursor: "pointer"}} onClick={() => navigate(`/folders/${one.USID}`)}>{one.Name}</Typography>
                                        <ChevronRightOutlined sx={{fontSize: 15, color: Grey5}}/>
                                    </React.Fragment>
                                ))}
                            </Box>
                            <Box sx={{display: "flex", alignItems: "center", gap: 1.5, pb: 1.5, borderBottom: "1px solid #E4E7EC"}}>
                                <FolderOpenOutlined sx={{fontSize: 26, color: Blue3}}/>
                                <Typography sx={{fontSize: 20, fontWeight: 500, color: Grey17}}>{folder.Name}</Typography>
                                <Tooltip title={`Your access: ${levelMeaning(folder.Level)}.`} placement="bottom">
                                    <Chip size="small" label={LEVEL_WORDS[folder.Level] ?? folder.Level} sx={{height: 20, fontSize: 11}}/>
                                </Tooltip>
                                {!folder.InheritGrants ? <Chip size="small" icon={<LockPersonOutlined sx={{fontSize: 14}}/>} label="Own grants only" sx={{height: 20, fontSize: 11}}/> : null}
                                <Box sx={{flexGrow: 1}}/>
                                {manage ? <>
                                    <Button size="small" startIcon={<CreateNewFolderOutlined/>} sx={{textTransform: "none"}} onClick={() => newChild(folder)}>New subfolder</Button>
                                    <Button size="small" startIcon={<EditOutlined/>} sx={{textTransform: "none"}} onClick={() => setDialog("rename")}>Rename</Button>
                                    <Button size="small" startIcon={<LockPersonOutlined/>} sx={{textTransform: "none"}} onClick={() => setDialog("access")}>Access</Button>
                                    <Button size="small" startIcon={<DeleteOutlineOutlined/>} sx={{textTransform: "none", color: Grey7}}
                                            onClick={() => api.delete("t1/v1/folders", {params: {USID: folder.USID}})
                                                .then(() => { loadTree(); navigate(folder.ParentFolderUSID ? `/folders/${folder.ParentFolderUSID}` : "/folders"); })
                                                .catch((problem: any) => setRefusal(refusalOf(problem, "Deleting it did not work.")))}>
                                        Delete
                                    </Button>
                                </> : null}
                            </Box>
                            {folder.Description ? <Typography sx={{fontSize: 13.5, color: Grey7, mt: 1.5}}>{folder.Description}</Typography> : null}
                            {refusal ? <Typography sx={{fontSize: 12.5, color: "#B3261E", mt: 1}}>{refusal}</Typography> : null}

                            <Box sx={{display: "flex", alignItems: "center", gap: 2, mt: 1}}>
                                <Tabs value={tab} onChange={(_, next) => setTab(next)} sx={{flexGrow: 1, minHeight: 36, "& .MuiTab-root": {minHeight: 36, textTransform: "none", fontSize: 13.5}}}>
                                    <Tab label={`Contents (${(folder.Children ?? []).length + (folder.Records ?? []).length})`}/>
                                    <Tab label="History"/>
                                </Tabs>
                                {tab === 0 ? <ViewSwitch view={view} onView={chooseView}/> : null}
                            </Box>

                            {tab === 0 ? (
                                <Box sx={{mt: 0.5}}>
                                    <FolderContents view={view} items={contents} columns={columns} recordDragging={recordDrag?.ObjectUSID} drop={contentDrop}
                                                    empty={folder.Level === "VIEW" ? "Nothing is filed here." : "Nothing is filed here. Records are filed from their own page, under Folder, or dragged here from another folder."}
                                                    onOpenFolder={one => navigate(`/folders/${one}`)} onOpenRecord={setDrawer}
                                                    mayFile={mayFile} onRecordDragStart={one => setRecordDrag({...one, FolderUSID: folder.USID})}
                                                    onRecordDragEnd={() => setRecordDrag(null)}/>
                                    {folder.RecordsNotShown ? (
                                        <Typography sx={{fontSize: 12.5, color: Grey5, mt: 1.5}}>
                                            {folder.RecordsNotShown} more record{folder.RecordsNotShown === 1 ? " is" : "s are"} filed here that your current view cannot open.
                                        </Typography>
                                    ) : null}
                                </Box>
                            ) : (
                                <Box sx={{mt: 1.5}}>
                                    {(history ?? []).map((one: Row, index: number) => (
                                        <Box key={index} sx={{display: "flex", gap: 2, py: 0.75, borderBottom: "1px solid #F2F4F7"}}>
                                            <Typography sx={{fontSize: 12.5, color: Grey5, width: 170, flexShrink: 0}}>{one.At ? new Date(one.At).toLocaleString() : ""}</Typography>
                                            <Typography sx={{fontSize: 13, color: Grey17, flexGrow: 1}}>{describeEvent(one)}</Typography>
                                            <Typography sx={{fontSize: 12.5, color: Grey7}}>{one.By}</Typography>
                                        </Box>
                                    ))}
                                    {history && !history.length ? <Typography sx={{fontSize: 13, color: Grey5}}>Nothing recorded yet.</Typography> : null}
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </div>
            <RecordDrawer revisionUSID={drawer} sessionContext={sessionContext} onClose={() => setDrawer(null)}/>
            </RecordDrawerContext.Provider>

            {dialog === "new" || dialog === "child" ? (
                <NameDialog title={dialog === "new" ? "New folder" : `New folder inside “${parentFor?.Name}”`} describe
                            onCancel={() => setDialog(null)}
                            onSave={(name, description) => api.post("t1/v1/folders", {
                                Name: name, Description: description, ...(dialog === "child" ? {ParentFolderUSID: parentFor?.USID} : {}),
                            }).then((answer: any) => { setDialog(null); loadTree(); navigate(`/folders/${answer.data.USID}`); })}/>
            ) : null}
            {refiling ? (
                <RefileDialog refiling={refiling} api={api} onCancel={() => setRefiling(null)}
                              onMoved={() => { setRefiling(null); loadTree(); loadFolder(); }}/>
            ) : null}
            {moving ? (
                <MoveDialog moving={moving} api={api} onCancel={() => setMoving(null)}
                            onMoved={() => { setMoving(null); loadTree(); loadFolder(); }}/>
            ) : null}
            {dialog === "rename" && folder ? (
                <NameDialog title="Rename and describe" initial={folder} describe onCancel={() => setDialog(null)}
                            onSave={(name, description) => api.put("t1/v1/folders", {USID: folder.USID, Name: name, Description: description})
                                .then((answer: any) => { setDialog(null); changed(answer.data); })}/>
            ) : null}
            {dialog === "access" && folder ? (
                <AccessDialog folder={folder} api={api} onClose={() => setDialog(null)} onChanged={changed}/>
            ) : null}
        </div>
    );
}
